import { type LineItem, useCartInfo } from "@contexts/CartInfoContext";
import { usePurchaseFlow } from "@contexts/PurchaseFlowContext";
import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { formatCurrency } from "@utils/currencyUtils";
import LogoIcon from "./LeafIcon";

type CartInfoDisplayProps = {
  disclaimer: string;
  isPurchaseCompletePage: boolean;
  handleChangeClick: () => void;
};

export function CartInfoDisplay({
  disclaimer,
  isPurchaseCompletePage,
  handleChangeClick,
}: CartInfoDisplayProps) {
  const {
    lineItems,
    subtotal,
    cartInfo,
    isTaxLoading,
    hasBillingPostalCode,
    selectedCurrency,
    isCartInfoLoading,
  } = useCartInfo();
  const { isOnConfirmStep } = usePurchaseFlow();

  // Always use optimistic lineItems for display
  // cartInfo is only used for tax, total_amount, discounts (server-calculated values)
  const displayItems: LineItem[] = lineItems;

  // Always use optimistic subtotal for display
  const displaySubtotal = subtotal;
  const tax = cartInfo.tax;
  const appliedDiscounts = cartInfo.applied_discounts;
  const bundles = cartInfo.bundles;
  const total = cartInfo.total_amount;

  // Calculate total discount for display total before tax is known
  const totalDiscount = appliedDiscounts?.reduce((sum, d) => sum + d.discount_amount, 0) ?? 0;
  const displayTotalBeforeTax = displaySubtotal - totalDiscount;

  console.log({ isOnConfirmStep, hasBillingPostalCode, isTaxLoading });

  // Skeleton item component for loading state
  const SkeletonItem = ({ key }: { key: number }) => (
    <CardHeader
      key={key}
      avatar={<Skeleton variant="circular" width={40} height={40} />}
      title={<Skeleton variant="text" width="70%" />}
      subheader={<Skeleton variant="text" width="50%" />}
      sx={{
        "& .MuiCardHeader-avatar": { alignSelf: "center" },
      }}
    />
  );

  return (
    <CardContent sx={{ pt: 0 }}>
      <Stack spacing={2} divider={<Divider />}>
        <div>
          {isCartInfoLoading ? (
            <>
              <SkeletonItem key={0} />
              <SkeletonItem key={1} />
              <SkeletonItem key={2} />
            </>
          ) : (
            displayItems.map((item, index) => {
              const suffix =
                item.frequency === "month"
                  ? "/month + tax"
                  : item.frequency === "year"
                    ? "/year + tax"
                    : "";

              const quantityPrefix = item.quantity > 1 ? `${item.quantity} x ` : "";
              const totalPrice = item.price * item.quantity;

              return (
                <CardHeader
                  key={item.sku || index}
                  avatar={<LogoIcon color="primary" />}
                  title={`${quantityPrefix}${item.title} – ${formatCurrency(totalPrice, selectedCurrency)}${suffix}`}
                  subheader={item.description}
                  slotProps={{ title: { fontWeight: 500 } }}
                  sx={{
                    "& .MuiCardHeader-avatar": { alignSelf: "center" },
                  }}
                />
              );
            })
          )}
        </div>

        <Stack spacing={1} alignItems="flex-end" textAlign="right">
          <Typography variant="body2" fontWeight={500}>
            Subtotal: {formatCurrency(displaySubtotal, selectedCurrency)}
          </Typography>

          {isTaxLoading ? (
            <Typography variant="body2" fontWeight={500}>
              Tax: <Skeleton variant="text" width={60} sx={{ display: "inline-block" }} />
            </Typography>
          ) : tax === undefined ? (
            <Typography
              variant="body2"
              fontWeight={300}
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              Taxes will be calculated based on your payment method location.
            </Typography>
          ) : (
            <Typography variant="body2" fontWeight={500}>
              Tax: {formatCurrency(tax, selectedCurrency)}
            </Typography>
          )}

          {appliedDiscounts?.map((discount) => (
            <Typography
              key={`discount-${discount.coupon_id}-${discount.discount_amount}`}
              variant="body2"
              color="text.secondary"
              fontWeight={500}
            >
              {discount.description}: -{formatCurrency(discount.discount_amount, selectedCurrency)}
            </Typography>
          ))}

          {!isTaxLoading && tax !== undefined ? (
            <Typography variant="h5" color="primary">
              Total ({selectedCurrency.toUpperCase()}): {formatCurrency(total, selectedCurrency)}
            </Typography>
          ) : isTaxLoading ? (
            <Typography variant="h5" color="primary">
              Total ({selectedCurrency.toUpperCase()}):{" "}
              <Skeleton variant="text" width={150} sx={{ display: "inline-block" }} />
            </Typography>
          ) : (
            <Typography variant="h5" color="primary">
              Total ({selectedCurrency.toUpperCase()}):{" "}
              {formatCurrency(displayTotalBeforeTax, selectedCurrency)} + tax
            </Typography>
          )}
        </Stack>

        <Stack spacing={2}>
          <Stack alignItems="flex-end">
            {bundles!.map((bundleItem) => {
              const price = bundleItem.price_amount;
              const title = bundleItem.product_title || "Untitled Product";
              const description = bundleItem.product_description || "";
              const frequency =
                bundleItem.frequency === "month"
                  ? "/month"
                  : bundleItem.frequency === "year"
                    ? "/year"
                    : "";

              const discountAmount = 0;

              // Create price display with strikethrough for discounted items
              const priceDisplay =
                discountAmount > 0 ? (
                  <>
                    <span
                      style={{ color: "red", textDecoration: "line-through", whiteSpace: "nowrap" }}
                    >
                      {formatCurrency(price, selectedCurrency)}{" "}
                    </span>
                    <span>
                      {formatCurrency(price - discountAmount, selectedCurrency)}
                      {frequency} + tax
                    </span>
                  </>
                ) : (
                  `${formatCurrency(price, selectedCurrency)}${frequency} + tax`
                );

              return (
                <CardHeader
                  key={bundleItem.sku}
                  avatar={<LogoIcon color="primary" />}
                  title={
                    <>
                      {bundleItem.quantity} x {title} {priceDisplay}
                    </>
                  }
                  subheader={description}
                  slotProps={{ title: { fontWeight: 500 } }}
                  sx={{
                    "& .MuiCardHeader-avatar": { alignSelf: "center" },
                  }}
                />
              );
            })}

            {disclaimer && (
              <Typography variant="caption" color="textSecondary" gutterBottom>
                {disclaimer}
              </Typography>
            )}
            {!isPurchaseCompletePage && (
              <Button
                variant="text"
                color="inherit"
                startIcon={<EditIcon fontSize="small" />}
                sx={{ mt: 1 }}
                onClick={handleChangeClick}
              >
                Change
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </CardContent>
  );
}
